import React, {useEffect, useMemo, useState} from 'react';
import {Avatar, Box, Button, Chip, Divider, Fab, Grid, IconButton, InputAdornment, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography, styled, useMediaQuery} from "@mui/material";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import AddIcon from "@mui/icons-material/Add";
import {useTheme} from "@mui/material/styles";
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from "@mui/icons-material/Search";
import MainCard from '../../../utils/ui-components/MainCard';
import CreatePackage from './create-package';
import CustomDrawerDialog from '../../../utils/ui-components/CustomDrawerDialog';
import { SuperAdminService } from '../../../assets/_services/super-admin-service';
import EmptyResultDataGrid from '../../../utils/ui-components/EmptyResultDataGrid';
import { gridSpacing } from '../../../store/constants';
import { render } from '@testing-library/react';
import { currencyFormat } from '../../../utils/utils';

interface Adminpackage {
    packageId : string;
    packageName : string;
    numberOfConcurrentCalls : number | string;
    numberOfUsers : number | string;
    outboundCallWithoutTaxes : number | string;
    rentalWithoutTaxes : number | string;
    is3CX : boolean;
    activeStatus : boolean;
}
const INITIAL_FORM_STATE : Adminpackage = {
    packageId : "",
    packageName : "",
    numberOfConcurrentCalls : "",
    numberOfUsers : "",
    outboundCallWithoutTaxes : "", 
    rentalWithoutTaxes : "",
    is3CX : false,
    activeStatus : true
}
function AdminPachages() {

    const theme: any = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [isLoading, setIsLoading] = useState(false)
    const [search, setSearch] = useState("");
    const [packageData, setPackageData]: any = useState([]);
    const [count, setCount] = useState(0);
    const [initialItem, setInitialItem] : any = useState(INITIAL_FORM_STATE)

    const [dialogTitle, setDialogTitle] = useState('');
    const [open, setOpen]: any = React.useState({
        top: false,
        left: false,
        bottom: false,
        right: false,
    });
    const [openView, setOpenView]: any = React.useState({
        top: false,
        left: false,
        bottom: false,
        right: false,
    });
       
    useEffect(() => {
        if(search === ""){
            fetchPackages();
        }else{
            searchPackageList()
        }
    }, [rowsPerPage,page])

    const fetchPackages = () => {
        setIsLoading(true)
        SuperAdminService.getPackagesforSuperAdmin(page,rowsPerPage).then(
            response => {
                if (response.isSuccess) {
                    setPackageData(response.data.data.rows)
                    setCount(response.data.data.count)
                    setIsLoading(false)
                }
            }
        )
    }

    const searchPackageList = () => {
        if(search === ""){
            fetchPackages()
        }else{
            setIsLoading(true)
            let formData ={
                packageName : search
            }
            SuperAdminService.searchPackage(formData, page,rowsPerPage).then(
                response => {
                    if (response.isSuccess) {
                        setPackageData(response.data.data.rows)
                        setCount(response.data.data.count)
                        setIsLoading(false)
                    }
                }
            )
        }
    }

    const handleChangePage = (newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (newPageSize: number) => {
        setRowsPerPage(newPageSize);
        setPage(0);
    };

    const handleClickOpen = (dialogTitle: string, initialstate : any) => {
        setOpen({...open, left: true});
        setDialogTitle(dialogTitle);
        setInitialItem(initialstate)
    };

    const handleClickOpenView = (dialogTitle: string, initialstate : any) => {
        setOpenView({...open, left: true});
        setDialogTitle(dialogTitle);
        setInitialItem(initialstate)
    };

    const drawer = useMemo(() => CustomDrawerDialog(CreatePackage)({
        anchor: 'right',
        open: open,
        setOpen: setOpen,
        dialogTitle: dialogTitle,
        fetchData: fetchPackages,
        theme: theme,
        initialItem : initialItem,
        width: matchDownSM ? '100vw' : null,
    }), [open]);

    const columns = [
        {
            field: "id",
            filterable: true,
            headerName: "ID",
            sortable: true,
            disableExport: false,
            minWidth: 50,
            flex: 1,
        },
        {
            field: "packageName",
            filterable: true,
            headerName: "Name",
            sortable: false,
            disableExport: false,
            minWidth: 150,
            flex: 3,
        },
        {
            field: "numberOfConcurrentCalls",
            filterable: true,
            headerName: "Concurrent calls",
            sortable: true,
            disableExport: false,
            minWidth: 130,
            flex: 2,
            renderCell : (params:any) => {
                return <div style={{ textAlign: 'right', width: '100%', paddingRight: 10 }}>{params?.row?.numberOfConcurrentCalls}</div>
            }
        },
        {
            field: "numberOfUsers",
            filterable: true,
            headerName: "Users",
            sortable: true,
            disableExport: false,
            minWidth: 100,
            flex: 2,
            renderCell : (params:any) => {
                return <div style={{ textAlign: 'right', width: '100%', paddingRight: 10 }}>{params?.row?.numberOfUsers}</div>
            }
        },
        {
            field: "outboundCallWithoutTaxes",
            filterable: true,
            headerName: "Outbound call without taxes",
            sortable: false,
            disableExport: false,
            minWidth: 220,
            flex: 4,
            renderCell : (params:any) => {
                return <div style={{ textAlign: 'right', width: '100%', paddingRight: 10 }}>{currencyFormat(params?.row?.outboundCallWithoutTaxes,"",2)}</div>
            }
        },
        {
            field: "rentalWithoutTaxes",
            filterable: true,
            headerName: "Rental Without taxes",
            sortable: false,
            disableExport: false,
            minWidth: 200,
            flex: 4,
            renderCell : (params:any) => {
                return <div style={{ textAlign: 'right', width: '100%', paddingRight: 10 }}>{currencyFormat(params?.row?.rentalWithoutTaxes,"",2)}</div>
            }
        },
        {
            field: "is3CX",
            filterable: true,
            headerName: "3CX",
            sortable: false,
            disableExport: false,
            minWidth: 100,
            flex: 2,
            renderCell: (params: any) => {
                let data = params.row.is3CX
                return (
                    <div style={{ textAlign: 'center', width: '100%', paddingRight: 10 }}>
                        {data ? 
                            <Typography variant="overline"
                            color='success.main'>Yes</Typography> :
                            <Typography variant="overline"
                            color='error.main'>No</Typography>
                        }
                    </div>
                );
            }
        },
        {
            field: "activeStatus",
            filterable: true,
            headerName: "Status",
            sortable: false,
            disableExport: false,
            minWidth: 100,
            flex: 2,
            renderCell: (params: any) => {
                let data = params.row.activeStatus
                return (
                    <>
                        {data ? 
                            <Typography variant="overline"
                            color='success.main'>Active</Typography> :
                            <Typography variant="overline"
                            color='error.main'>Inactive</Typography>
                        }
                    </>
                );
            }
        },
        {
            field: "view",
            hide: false,
            filterable: false,
            headerName: "View",
            sortable: false,
            renderCell: (params: any) => {
                let data = params.row
                const initialItem = {
                    packageName : data.packageName,
                    packageId : data._id,
                    numberOfConcurrentCalls : data.numberOfConcurrentCalls,
                    numberOfUsers : data.numberOfUsers,
                    outboundCallWithoutTaxes : data.outboundCallWithoutTaxes,
                    rentalWithoutTaxes : data.rentalWithoutTaxes,
                    is3CX : data.is3CX === 1 ? true : false,
                    activeStatus : data.activeStatus === 1 ? true : false

                }
                return (
                    <Tooltip title='Edit'>
                        <IconButton
                            onClick={() => {
                                handleClickOpen(`Update Package`, initialItem)
                            }}
                        >
                            <EditIcon
                                fontSize="inherit"
                                color="primary"
                            />
                        </IconButton>
                    </Tooltip>
                );
            },
            disableExport: true,
            disableColumnMenu: true,
            minWidth: 100,
            flex: 1,
        },
    ];

    return (
        <Grid container justifyContent={'center'}>
            <Grid item xs={12}>
                <Fab
                    color="primary"
                    sx={{position: "fixed", zIndex: 3, bottom: 16, right: 16}}
                    aria-label="add"
                    onClick={() => handleClickOpen(`Create Package`,INITIAL_FORM_STATE)}
                >
                    <AddIcon/>
                </Fab>
            </Grid>
            <Grid item xs={12}>
                <MainCard title="Packages">
                <Grid container spacing={gridSpacing} sx={{mb:1 }}>
                    <Grid item lg={4} md={5} sm={6} xs={9}>
                        <TextField
                            label="Search"
                            placeholder="Search Package name.."
                            value={search}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setSearch(event.target.value);
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon/>
                                    </InputAdornment>
                                )
                            }}
                            // sx={{...theme.typography.customInput}}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                        <Button
                            color="primary"
                            variant="contained"
                            // sx={{...theme.typography.customInput}}
                            type="button"
                            onClick={()=>{
                                setPage(0)
                                searchPackageList()
                            }}
                        >
                            View
                        </Button>
                    </Grid>
                </Grid>
                    <Box sx={{minHeight: 400, width: '100%'}}>
                        <DataGrid
                            autoHeight
                            columns={columns}
                            loading={isLoading}
                            rows={packageData.map((item : any, index : number) =>{ 
                                return{
                                    ...item,
                                    id : index + 1
                                }})}
                            components={{
                                Toolbar: GridToolbar,
                                NoRowsOverlay: EmptyResultDataGrid,
                                LoadingOverlay: LinearProgress
                            }}
                            componentsProps={{
                                toolbar: {
                                    csvOptions: {disableToolbarButton: true},
                                    printOptions: {disableToolbarButton: true},
                                    showQuickFilter: false,
                                    quickFilterProps: {debounceMs: 250},
                                },
                            }}
                            pagination
                            getRowId={(row) => row.id}
                            paginationMode="server"
                            rowCount={count}
                            rowsPerPageOptions={[5, 10, 25, 100]}
                            page={page}
                            pageSize={rowsPerPage}
                            onPageChange={handleChangePage}
                            onPageSizeChange={handleChangeRowsPerPage}
                        />
                    </Box>

                </MainCard>
            </Grid>
            
            {drawer}
            {/* {drawerView} */}
        </Grid>
    );
}

export default AdminPachages;